import { get } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import React, { Fragment } from "react";
import styled from "styled-components";
import { CgAsterisk } from "react-icons/cg";
import { Grid, MenuItem, Typography } from "@mui/material";

import { uploadToS3 } from "../../../utils";
import { ErrorButton } from "../../css components/Button";
import { colors } from "../../css components/Style";
import EditButtons from "../../../components/EditButtons";
import {
  AutocompleteMultiSelect,
  ButtonFooter,
  ComponentHeader,
  PageHeader,
  Paper,
  TextField,
} from "../../../components";
import FormTwo from "./FormTwo";
import { alert } from "../../../redux/slices/alertSlice";
import { BrandStatusToggle } from "./ToggleBrand";

const AllowedImage = styled(Typography)`
  color: #333;
  font-size: 15px;
  opacity: 0.8;
  margin-top: 15px;
`;

const Logo = styled("img")({
  width: "100%",
  height: "auto",
  maxWidth: "120px",
  objectFit: "contain",
});

export default function FormOne({
  setEditState,
  list,
  formikProps,
  editState,
  multiBrand,
  businessEntityID,
}) {
  const dispatch = useDispatch();
  const agencyID =
    useSelector((state) => state.memberDetails.agencyID) ||
    sessionStorage.getItem("agencyID");

  return (
    <React.Fragment>
      <Grid size={12}>
        <PageHeader
          buttons={[
            !editState && (
              <EditButtons editState={editState} setEditState={setEditState} />
            ),
          ]}
          pageName={"Brand Details"}
          pageHeader={
            !formikProps.values.brandID
              ? "Enter brand details to set up identity and start managing campaigns."
              : "View and manage your brand information."
          }
        />
      </Grid>

      <Grid size={12}>
        <Paper>
          <Grid size={12}>
            <ComponentHeader
              title={"Brand Details"}
              buttons={[
                formikProps.values.brandID && (
                  <BrandStatusToggle
                    editState={editState}
                    businessEntityID={businessEntityID}
                    formikProps={formikProps}
                  />
                ),
              ]}
            />
          </Grid>

          <Grid item textAlign="center" display="flex">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Logo
                src={
                  formikProps.values.brandAvatar
                    ? formikProps.values.brandAvatar
                    : ""
                }
              />
            </div>
          </Grid>

          {editState && (
            <Grid item alignSelf={"center"}>
              <div style={{ display: "flex" }}>
                <input
                  disabled={!editState}
                  accept=".jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  id="avatar-button-file"
                  multiple
                  onChange={(event) => handleFileChange(event, "brandAvatar")}
                  type="file"
                />
                <label htmlFor="avatar-button-file">
                  <div
                    className="add-new-logo"
                    variant="contained"
                    style={{
                      marginRight: "10px",
                      backgroundColor: colors.publicisBlue,
                    }}
                  >
                    Add New Logo
                  </div>
                </label>

                {formikProps.values.brandAvatar && (
                  <ErrorButton
                    variant="outlined"
                    onClick={() => {
                      formikProps.setValues((prev) => ({
                        ...prev,
                        brandAvatar: "",
                      }));
                    }}
                  >
                    Reset
                  </ErrorButton>
                )}
              </div>

              <AllowedImage>
                Allowed JPG, GIF or PNG
                {!formikProps.values.brandAvatar && !agencyID && (
                  <CgAsterisk style={{ color: colors.error }} />
                )}
                .
              </AllowedImage>
            </Grid>
          )}

          <Grid size={12} />

          <Grid size={4}>
            <TextField
              label="Brand Name"
              disabled={!editState}
              formikProps={formikProps}
              placeholder="Brand Name"
              name="brandName"
              required
              inputProps={{ maxLength: 35 }}
            />
          </Grid>

          <Grid size={4}>
            <TextField
              label="Brand Website"
              disabled={!editState}
              formikProps={formikProps}
              name="website"
              placeholder="Website"
              inputProps={{ "aria-label": "Without label" }}
            />
          </Grid>

          <Grid size={4}>
            <TextField
              select
              label="Brand Status"
              disabled={!editState}
              formikProps={formikProps}
              placeholder="Brand Status"
              name="isVisible"
              required
            >
              <MenuItem value={"true"}>Active</MenuItem>
              <MenuItem value={"false"}>Inactive</MenuItem>
            </TextField>
          </Grid>

          {agencyID && (
            <Fragment>
              <Grid size={4}>
                <AutocompleteMultiSelect
                  required={agencyID ? false : true}
                  name="siteCategory"
                  label={"Business Category"}
                  disabled={!editState}
                  forcePopupIcon={!editState ? false : true}
                  formikProps={formikProps}
                  defaults={{
                    primaryKey: "name",
                    displayLabel: "name",
                  }}
                  options={list?.categories}
                  placeholder="Select Category"
                  onChange={(_, arr, reason, option) => {
                    handleChange(arr, option, reason, "siteCategory", "name");
                  }}
                />
              </Grid>

              {formikProps.values.siteCategory.includes("Others") && (
                <Fragment>
                  <Grid size={4}>
                    <TextField
                      required
                      disabled={!editState}
                      formikProps={formikProps}
                      name="others_category"
                      label={"Enter your other category"}
                      placeholder={"Enter your other category"}
                    />
                  </Grid>
                </Fragment>
              )}
            </Fragment>
          )}

          <FormTwo
            agencyID={agencyID}
            list={list}
            multiBrand={multiBrand}
            formikProps={formikProps}
            editState={editState}
          />
        </Paper>

        <Grid size={12}>
          <ButtonFooter
            ID={formikProps.values.brandID}
            editState={editState}
            setEditState={setEditState}
            formikProps={formikProps}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );

  function handleChange(arr, { option }, reason, name, displayName) {
    formikProps.setValues((prevVal) => ({
      ...prevVal,
      [name]: setValuesOfDropDown(prevVal[name]),
    }));

    function setValuesOfDropDown(val) {
      let values = [...val];

      if (reason === "removeOption") {
        const nameToDropDown = {
          audienceLocation: "country_code",
          siteCategory: "name",
          audienceLanguages: "code",
        };

        let dropDownName = nameToDropDown[name];

        if (option[dropDownName]) {
          values = [
            ...values.filter((items) => items !== option[dropDownName]),
          ];
        } else {
          values.pop();
        }
      }
      if (reason === "selectOption") {
        values.push(option[displayName]);
      }

      return values;
    }
  }

  function handleFileChange({ target }, name) {
    let allowedExtensions = ["jpg", "jpeg", "png"];
    let folderPath = "company_document/" + new Date().getTime() + "_";
    const file = get(target, "files[0]", "");

    let fileName = file?.name?.replace(/[&\/\\#,+()$~%'":*?<>{}]/g, "");
    let extension = fileName.split(".").pop();
    if (allowedExtensions.includes(extension)) {
      const uploadBucket = {
        name: "adn-media",
        region: "ap-south-1",
        identityPoolID: "ap-south-1:e58786e7-9711-4dea-a2e2-7d9c10e38333",
      };

      uploadToS3({
        bucket: uploadBucket,
        filePath: folderPath + fileName,
        file,
      }).then((response) => {
        if (response.status) {
          formikProps.setFieldValue(
            [name],
            "https://" +
              uploadBucket.name +
              ".s3.ap-south-1.amazonaws.com/" +
              folderPath +
              fileName
          );
        }
      });
    } else {
      dispatch(
        alert({
          type: "error",
          message: "Invalid Extension. Only .jpg, .jpeg, .png file allowed.",
        })
      );
    }
  }
}
